import type { ApolloClient } from "@apollo/client";
import {
  CefrScale,
  CreateCertificateDocument,
  CreateEducationDocument,
  CreateExperienceDocument,
  CreateExpertiseDocument,
  CreateLanguageSkillDocument,
  UpdateCertificateDocument,
  UpdateEducationDocument,
  UpdateExperienceDocument,
  UpdateLanguageSkillDocument,
  UpdatePersonDocument,
  type UpdateCertificateDataInput,
  type UpdateEducationDataInput,
  type UpdateExperienceDataInput,
  type UpdateLanguageSkillDataInput,
  type UpdatePersonDataInput,
} from "@/graphql/generated/operations";
import { toDateInstant } from "@/lib/date-instant";
import { dataForField, type DataPatch, type ProfileKey } from "@/lib/person-data";
import type { CollectionKey, ImportResolution, PreviewData, RowDiff } from "./profile-merge-types";

// Persists an in-line LinkedIn merge review through the platform's CANONICAL
// profile mutations — genesis no longer exposes a server-side commit/snapshot
// (the pivot deleted commitProfileImport), so the client applies the user's
// accepted resolutions itself:
//   - person_detail  → one batched UpdatePerson (partial patch: only chosen fields)
//   - collection row → CreateX for an ADD, UpdateX(existingRowId) for a CONFLICT
// Each accepted item is one mutation; they fire concurrently and each records
// its own outcome, so a single failure never rolls back the rest. The caller
// prunes the saved items from the review (see dropResolved) so a retry only
// re-sends the failures and never re-creates an already-created row.
//
// Value sourcing: `resolutions` (from resolutionsFor) carries each item's
// keep/use choice plus in-place edits; `preview` carries the full incoming
// field values a Create needs. KEEP_EXISTING person fields with an edited
// existing side ARE written (their edit rides overrideValue); KEEP_EXISTING
// collection rows are left untouched here (Pass-2 existing-side edits land as a
// partial UpdateX; see the store's existing-side row overrides).
//
// New rows are private by default (no sharedWith); the user shares explicitly
// afterwards. Imported experiences carry no industry (genesis allows null) —
// the field is simply omitted. Education degree values are stable catalog ids
// in ProfileImportPreview and are written as DegreeRelationInput.

export interface CommitArgs {
  // Narrowed to `mutate` so tests can pass a lightweight fake client.
  client: Pick<ApolloClient, "mutate">;
  personId: string;
  // Position for the first imported skill; further skills increment from here so
  // they sort after the user's existing expertises.
  expertiseBase: number;
  preview: PreviewData;
  resolutions: ImportResolution[];
}

export interface CommitResult {
  savedKeys: string[];
  failedKeys: string[];
}

// A merge person_detail diff key → the profile edit key (ProfileKey) it commits
// as. The shared `dataForField` then maps that ProfileKey to the actual
// UpdatePerson patch — a reference field → `{ id }`, citizenship → an id-list,
// childCount → an int, everything else a nullable scalar — so an IMPORTED field
// (LinkedIn or CV) commits byte-identically to the manual editor. Exported so a
// test asserts it covers every carded guide step (DETAIL_KEY_ORDER): a carded
// field missing here would be decidable but unsavable. The first block is the
// LinkedIn preview's keys (mapping unchanged from before the CV work); the rest
// are the additional CV-extractor person fields.
export const MERGE_KEY_TO_PROFILE_KEY: Record<string, ProfileKey> = {
  // LinkedIn + shared
  phone: "phone",
  email: "email",
  bio: "summary",
  dateOfBirth: "birthday",
  profession: "profession",
  street: "street",
  zipCode: "zipCode",
  jobTitle: "role",
  currentEmployer: "currentEmployer",
  // CV-extractor additions
  gender: "gender",
  maritalStatus: "maritalStatus",
  childCount: "childCount",
  nationality: "nationality",
  citizenship: "citizenship",
  website: "website",
  city: "city",
  country: "country",
  houseNumber: "houseNumber",
  apartment: "apartment",
  currentActivity: "currentActivity",
  drivingLicence: "drivingLicence",
};

const SECTIONS: CollectionKey[] = [
  "experiences",
  "educations",
  "languageSkills",
  "certificates",
  "expertises",
];

interface Task {
  keys: string[];
  run: () => Promise<unknown>;
}

// The effective incoming value of one row field: the user's edit if present,
// else the pristine incoming value. null when the field is absent/blank.
function valOf(row: RowDiff, overrides: Map<string, string>, field: string): string | null {
  if (overrides.has(field)) return overrides.get(field) ?? null;
  return row.fields.find((f) => f.field === field)?.incomingValue ?? null;
}

// A LinkedIn language "level" is either a CEFR grade (persisted as cefr) or the
// synthetic NATIVE rung (persisted as nativeSpeaker, no cefr — genesis has no
// NATIVE enum). Anything unrecognised falls back to no proficiency.
function cefrNative(level: string | null): { nativeSpeaker: boolean; cefr: CefrScale | null } {
  if (level === "NATIVE") return { nativeSpeaker: true, cefr: null };
  const cefr = level && (Object.values(CefrScale) as string[]).includes(level) ? (level as CefrScale) : null;
  return { nativeSpeaker: false, cefr };
}

// genesis stores an experience's tasks as a string LIST but the diff carries it
// as one "\n"-joined "description" TEXT field, so split it back on save (mirrors
// the profile editor's linesToList) — otherwise a multi-item list collapses to
// one element.
function activitiesList(description: string | null): string[] {
  return (description ?? "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line !== "");
}

// endDate is the one date field whose BLANK carries meaning ("present"/ongoing).
// A blank only counts as an explicit "present" when the user actually edited the
// field (overridden) → send null; otherwise a blank is just "not provided" and is
// omitted (`undefined`) so the partial patch preserves the saved end date. Shared
// by experiences AND educations so the two mappers can't drift apart.
function endDatePatch(
  val: (field: string) => string | null,
  overridden: Set<string>,
): ReturnType<typeof toDateInstant> | undefined {
  if (overridden.has("endDate")) return toDateInstant(val("endDate") ?? "");
  return toDateInstant(val("endDate") ?? "") ?? undefined;
}

// A CREATE for one accepted ADD row: the full incoming values plus the person
// relation, no sharedWith (private by default). null when a required field the
// import lacks (an experience/education without a start date, etc.).
function createRun(
  client: Pick<ApolloClient, "mutate">,
  personId: string,
  section: CollectionKey,
  v: (field: string) => string | null,
  skillPosition: number,
): (() => Promise<unknown>) | null {
  const person = { id: personId };
  switch (section) {
    case "experiences": {
      const jobTitle = v("title");
      const startDate = toDateInstant(v("startDate") ?? "");
      if (!jobTitle || !startDate) return null;
      return () =>
        client.mutate({
          mutation: CreateExperienceDocument,
          variables: {
            input: {
              data: {
                jobTitle,
                employer: v("employer"),
                location: v("location"),
                startDate,
                endDate: toDateInstant(v("endDate") ?? ""),
                activities: activitiesList(v("description")),
                person,
              },
            },
          },
        });
    }
    case "educations": {
      const field = v("fieldOfStudy");
      const startDate = toDateInstant(v("startDate") ?? "");
      if (!field || !startDate) return null;
      const degree = v("degree");
      return () =>
        client.mutate({
          mutation: CreateEducationDocument,
          variables: {
            input: {
              data: {
                field,
                institution: v("institution"),
                degree: degree ? { id: degree } : null,
                startDate,
                endDate: toDateInstant(v("endDate") ?? ""),
                person,
              },
            },
          },
        });
    }
    case "languageSkills": {
      const languageTag = v("language");
      if (!languageTag) return null;
      return () =>
        client.mutate({
          mutation: CreateLanguageSkillDocument,
          variables: { input: { data: { languageTag, ...cefrNative(v("level")), selfAssessed: false, person } } },
        });
    }
    case "certificates": {
      const name = v("name");
      if (!name) return null;
      return () =>
        client.mutate({
          mutation: CreateCertificateDocument,
          variables: { input: { data: { name, date: toDateInstant(v("issued") ?? ""), person } } },
        });
    }
    case "expertises": {
      const description = v("skill");
      if (!description) return null;
      return () =>
        client.mutate({
          mutation: CreateExpertiseDocument,
          variables: { input: { data: { description, position: skillPosition, level: 0, person } } },
        });
    }
  }
}

// A NON-DESTRUCTIVE partial UPDATE of a matched saved row (existingRowId), used
// both when a CONFLICT row is taken from LinkedIn and when a KEPT row's existing
// side was edited. `val` supplies the effective value per field (incoming or
// existing, override-applied) and `overridden` is the set of fields the user
// explicitly edited. Only the required field(s) plus fields that carry a real
// value or an explicit edit are sent — genesis patches exactly the provided
// fields, so a value the source lacks (an unfilled employer, a saved
// self-assessment) is left untouched rather than nulled. null when a required
// field is missing, or for skills (matched by name → nothing to update).
function updateRun(
  client: Pick<ApolloClient, "mutate">,
  section: CollectionKey,
  id: string,
  val: (field: string) => string | null,
  overridden: Set<string>,
): (() => Promise<unknown>) | null {
  // Include an optional field only when it carries a real value or was edited;
  // otherwise omit it so the partial patch preserves the saved value.
  const keep = (field: string) => {
    const raw = val(field);
    return (raw !== null && raw.trim() !== "") || overridden.has(field);
  };
  switch (section) {
    case "experiences": {
      const jobTitle = val("title");
      if (!jobTitle) return null;
      const data: UpdateExperienceDataInput = { jobTitle };
      if (keep("employer")) data.employer = val("employer") || null;
      if (keep("location")) data.location = val("location") || null;
      const startDate = toDateInstant(val("startDate") ?? "");
      if (startDate) data.startDate = startDate;
      const endDate = endDatePatch(val, overridden);
      if (endDate !== undefined) data.endDate = endDate;
      const description = val("description");
      if (description && description.trim() !== "") data.activities = activitiesList(description);
      return () => client.mutate({ mutation: UpdateExperienceDocument, variables: { id, input: { data } } });
    }
    case "educations": {
      const field = val("fieldOfStudy");
      if (!field) return null;
      const data: UpdateEducationDataInput = { field };
      if (keep("institution")) data.institution = val("institution") || null;
      if (keep("degree")) {
        const degree = val("degree");
        data.degree = degree ? { id: degree } : null;
      }
      const startDate = toDateInstant(val("startDate") ?? "");
      if (startDate) data.startDate = startDate;
      const endDate = endDatePatch(val, overridden);
      if (endDate !== undefined) data.endDate = endDate;
      return () => client.mutate({ mutation: UpdateEducationDocument, variables: { id, input: { data } } });
    }
    case "languageSkills": {
      const languageTag = val("language");
      if (!languageTag) return null;
      // selfAssessed is deliberately omitted so a saved self-assessment survives.
      const data: UpdateLanguageSkillDataInput = { languageTag, ...cefrNative(val("level")) };
      return () => client.mutate({ mutation: UpdateLanguageSkillDocument, variables: { id, input: { data } } });
    }
    case "certificates": {
      const name = val("name");
      if (!name) return null;
      const data: UpdateCertificateDataInput = { name };
      const date = toDateInstant(val("issued") ?? "");
      if (date) data.date = date;
      return () => client.mutate({ mutation: UpdateCertificateDocument, variables: { id, input: { data } } });
    }
    case "expertises":
      return null;
  }
}

export async function commitProfileMerge({
  client,
  personId,
  expertiseBase,
  preview,
  resolutions,
}: CommitArgs): Promise<CommitResult> {
  const byKey = new Map(resolutions.map((r) => [r.key, r]));
  const tasks: Task[] = [];
  // An item that cannot be built or mapped counts as FAILED rather than silently
  // vanishing from both saved and failed (which would let the bar reset() and
  // lose it with no error).
  const reject = (label: string) => () => Promise.reject(new Error(`cannot save ${label}`));

  // person_detail → one batched UpdatePerson carrying every accepted field.
  const personData: DataPatch = {};
  const personKeys: string[] = [];
  const unmappedKeys: string[] = [];
  for (const diff of preview.personDetail) {
    if (diff.action === "UNCHANGED") continue;
    const resolution = byKey.get(diff.key);
    if (!resolution) continue;
    // The value this resolution would write, or undefined when it writes nothing
    // (a KEEP_EXISTING field with no in-place edit — leave the saved value).
    let value: string | null | undefined;
    if (resolution.choice === "USE_INCOMING") value = resolution.overrideValue ?? diff.incomingValue;
    else if (resolution.overrideValue !== undefined) value = resolution.overrideValue;
    else continue;
    const profileKey = MERGE_KEY_TO_PROFILE_KEY[diff.key];
    if (!profileKey) {
      // A decided, would-write field with no profile mapping: account for it as
      // failed instead of dropping it (build-time guarded by the
      // DETAIL_KEY_ORDER ⊆ MERGE_KEY_TO_PROFILE_KEY test).
      unmappedKeys.push(diff.key);
      continue;
    }
    // dataForField builds the same UpdatePerson patch the manual editor uses (a
    // reference field → { id }, citizenship → an id-list, etc.). A null value
    // clears the field ("" → null for nullable scalars).
    Object.assign(personData, dataForField(profileKey, value ?? ""));
    personKeys.push(diff.key);
  }
  if (personKeys.length > 0) {
    tasks.push({
      keys: personKeys,
      run: () =>
        client.mutate({
          mutation: UpdatePersonDocument,
          variables: { id: personId, input: { data: personData as UpdatePersonDataInput, dryRun: false } },
        }),
    });
  }
  if (unmappedKeys.length > 0) tasks.push({ keys: unmappedKeys, run: reject(unmappedKeys.join(", ")) });

  // collection rows → one Create/Update each.
  let skillPosition = expertiseBase;
  for (const section of SECTIONS) {
    for (const row of preview[section]) {
      if (row.action === "UNCHANGED") continue;
      const resolution = byKey.get(row.rowKey);
      if (!resolution) continue;
      const create = row.action === "ADD" || row.existingRowId === null;

      if (resolution.choice === "USE_INCOMING") {
        const overrides = new Map((resolution.fieldOverrides ?? []).map((o) => [o.field, o.value]));
        const v = (field: string) => valOf(row, overrides, field);
        if (create) {
          // Only a created skill consumes a position (else creates get gaps).
          const position = section === "expertises" ? skillPosition++ : 0;
          const run = createRun(client, personId, section, v, position);
          tasks.push({ keys: [row.rowKey], run: run ?? reject(row.rowKey) });
        } else if (section === "expertises") {
          // A matched skill already exists under that name — nothing to update.
          tasks.push({ keys: [row.rowKey], run: () => Promise.resolve() });
        } else {
          const run = updateRun(client, section, row.existingRowId!, v, new Set(overrides.keys()));
          tasks.push({ keys: [row.rowKey], run: run ?? reject(row.rowKey) });
        }
      } else if (resolution.existingFieldOverrides?.length && row.existingRowId) {
        // KEPT row whose saved side was edited in place → non-destructive partial
        // update sourced from the existing values + the edits.
        const o = new Map(resolution.existingFieldOverrides.map((x) => [x.field, x.value]));
        const ev = (field: string): string | null =>
          o.has(field) ? (o.get(field) ?? null) : (row.fields.find((f) => f.field === field)?.existingValue ?? null);
        const run = updateRun(client, section, row.existingRowId, ev, new Set(o.keys()));
        tasks.push({ keys: [row.rowKey], run: run ?? reject(row.rowKey) });
      }
    }
  }

  const settled = await Promise.allSettled(tasks.map((t) => t.run()));
  const savedKeys: string[] = [];
  const failedKeys: string[] = [];
  settled.forEach((outcome, i) => {
    const target = outcome.status === "fulfilled" ? savedKeys : failedKeys;
    target.push(...tasks[i]!.keys);
  });
  return { savedKeys, failedKeys };
}
